import { SectionHead, Stagger, StaggerItem, Reveal } from "@/lib/motion";
import { ArrowRight, Check, MonitorSmartphone } from "lucide-react";
import { scrollToTarget } from "@/components/SmoothScroll";
import { requestContactPrefill } from "@/lib/contactBus";

const PLANS = [
  { who: "1 à 10 employés", amt: "50 000" },
  { who: "11 à 20 employés", amt: "75 000" },
  { who: "21 à 35 employés", amt: "100 000" },
];

const FEATURES = ["Un expert RH dédié", "Un expert paie dédié", "Gestion externe de la paie et des RH"];

export default function Pricing() {
  return (
    <section id="tarifs" className="border-y border-white/10 bg-korban-navy2/25 py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5">
        <SectionHead
          tag="Nos tarifs"
          title="Assistance en gestion externe de la paie et des ressources humaines."
          sub="Un tarif mensuel simple, selon la taille de votre entreprise."
        />
        <Stagger className="grid gap-6 md:grid-cols-3">
          {PLANS.map((p, i) => (
            <StaggerItem key={p.who} className="h-full">
              <article
                className={`flex h-full flex-col rounded-2xl border bg-white/[0.04] p-8 text-center transition-all duration-300 hover:-translate-y-1.5 hover:border-korban-gold/50 ${
                  i === 1 ? "border-korban-gold/40 shadow-[0_20px_50px_rgba(249,178,51,0.08)]" : "border-white/10"
                }`}
                data-testid={`pricing-plan-${i + 1}`}
              >
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-300">{p.who}</p>
                <p className="mt-5 font-display text-5xl font-black tracking-tight text-white">
                  {p.amt}
                  <span className="mt-2 block font-sans text-xs font-semibold uppercase tracking-widest text-korban-gold">
                    FCFA TTC / mois
                  </span>
                </p>
                <ul className="mt-7 flex-1 space-y-3 text-left">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[0.93rem] text-slate-200">
                      <Check size={16} className="mt-0.5 shrink-0 text-korban-blue" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToTarget("#contact");
                  }}
                  data-testid={`pricing-cta-${i + 1}`}
                  className="mt-8 inline-flex items-center justify-center rounded-md bg-korban-blue px-6 py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-korban-gold hover:text-korban-navy"
                >
                  Demander ce forfait
                </a>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-10 max-w-3xl text-center text-[0.93rem] leading-relaxed text-slate-400">
            Tous les prix s'entendent toutes taxes comprises (TTC). Chaque forfait donne droit à l'assistance d'un
            expert RH et d'un expert paie de KORBAN CONSULTING. Plus de 35 employés ? Contactez-nous pour une offre
            adaptée.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div
            className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10"
            data-testid="pricing-softonly-panel"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-korban-navy2/70 ring-1 ring-korban-gold/30">
                <MonitorSmartphone size={22} className="text-korban-gold" />
              </span>
              <h3 className="font-display text-2xl font-semibold leading-snug text-white">
                Logiciel seul — sans assistance en gestion externe
              </h3>
            </div>
            <p className="mt-3 max-w-3xl text-[0.95rem] leading-relaxed text-slate-300">
              Tarif mensuel pour les entreprises qui souhaitent utiliser uniquement le logiciel Boussole de la Paie
              (SIRH-CI/PAIE), sans assistance en gestion externe de la paie et des ressources humaines.
            </p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {[
                { who: "De 1 à 10 employés", amt: "25 000" },
                { who: "De 11 à 35 employés", amt: "35 000" },
              ].map((p) => (
                <div
                  key={p.who}
                  className="flex items-center justify-between gap-4 rounded-xl bg-korban-navy2/40 px-6 py-5"
                  data-testid={`softonly-price-${p.who.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                  <span className="text-sm font-bold uppercase tracking-wider text-slate-200">{p.who}</span>
                  <span className="whitespace-nowrap font-display text-2xl font-black text-korban-gold">
                    {p.amt} <small className="font-sans text-[0.62rem] font-semibold text-slate-400">FCFA / mois</small>
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs leading-relaxed text-slate-400">
              Ces montants concernent uniquement l'utilisation du logiciel Boussole de la Paie (SIRH-CI/PAIE).
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                requestContactPrefill(
                  "Bonjour, je souhaite souscrire au logiciel Boussole de la Paie (SIRH-CI/PAIE)."
                );
                setTimeout(() => scrollToTarget("#contact"), 60);
              }}
              data-testid="softonly-subscribe-button"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-korban-gold px-7 py-3.5 font-bold text-korban-navy shadow-[0_10px_30px_rgba(249,178,51,0.25)] transition-all hover:-translate-y-0.5 hover:bg-korban-golddark"
            >
              Souscrire au logiciel <ArrowRight size={17} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
